import type { MonitoramentoItem, UsuarioWorkflow } from './models';

/** Nível mínimo (além do perfil de TI) autorizado a tratar contas vencidas na Governança (N5 — Diretor de Controladoria). */
export const NIVEL_MINIMO_CONTAS_VENCIDAS = 5;

/**
 * Quem vê as ações sobre contas vencidas: Administrador de TI ou nível superior (>= N5).
 * A tela só esconde o botão; o backend continua validando alçada e segregação de funções em cada aprovação.
 */
export const podeGerirContasVencidas = (perfil?: Pick<UsuarioWorkflow, 'adminTi' | 'nivel'> | null): boolean =>
  !!perfil && (!!perfil.adminTi || (perfil.nivel ?? 0) >= NIVEL_MINIMO_CONTAS_VENCIDAS);

/** Contas (conciliações) com prazo de SLA vencido, as mais atrasadas primeiro. */
export const contasVencidas = (itens: MonitoramentoItem[]): MonitoramentoItem[] =>
  itens.filter(i => i.slaStatus === 'VERMELHO').sort((a, b) => a.horasRestantes - b.horasRestantes);

export type AcaoContaVencida = 'APROVAR' | 'PRORROGAR_PRAZO' | 'BLOQUEAR' | 'DEVOLVER_AO_AUTOR';

export const rotuloAcaoContaVencida: Record<AcaoContaVencida, string> = {
  APROVAR: 'Aprovar',
  PRORROGAR_PRAZO: 'Prorrogar prazo',
  BLOQUEAR: 'Bloquear',
  DEVOLVER_AO_AUTOR: 'Devolver ao autor',
};

/**
 * Valida o formulário (mesmas regras que o backend, para avisar antes de enviar) e monta o corpo do pedido.
 * `novoPrazo` vem do campo de data (AAAA-MM-DD); o prazo vale até o fim desse dia.
 */
export const montarPedidoContaVencida = (
  acao: Exclude<AcaoContaVencida, 'APROVAR'>, justificativa: string, novoPrazo: string, agora: Date = new Date(),
): { erro: string } | { corpo: { acao: string; justificativa: string; novoPrazo?: string } } => {
  if (!justificativa.trim()) return { erro: 'Informe a justificativa SOX.' };
  if (acao !== 'PRORROGAR_PRAZO') return { corpo: { acao, justificativa: justificativa.trim() } };
  if (!novoPrazo) return { erro: 'Informe o novo prazo (SLA).' };
  const fim = new Date(`${novoPrazo}T23:59:00`);
  if (Number.isNaN(fim.getTime()) || fim <= agora) return { erro: 'O novo prazo deve ser uma data futura.' };
  return { corpo: { acao, justificativa: justificativa.trim(), novoPrazo: fim.toISOString() } };
};
